import {
  Stack,
  Typography,
  Button,
  Divider,
  Box,
  Grid,
  Checkbox,
  Link,
  IconButton,
  SvgIcon,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import AddContactDropezoneComponent from "../AddContacts/Components/AddContactDropezoneComponent";
import { useState } from "react";
import { uploadContactsField, uploadContactsValidation } from "../Helpers";
import { ContactContext } from "../AddContacts/Context";
import DownloadCSV_Icon from "assets/icons1/DownloadCSV_Icon.svg";
import ExportCSV, {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { selectEmail } from "redux/authSlice";
import { useAppSelector } from "utils/hooks";
import DynamicCsvFieldMapping from "../AddContacts/Components/DynamicCsvFieldMapping";
import GroupNameTypeSelection from "../AddContacts/Components/GroupNameTypeSelection";
import strings from "global/constants/StringConstants";
import { CustomSwitch } from "screens/Settings/Users/ViewUser";
import { uploadContactGroups } from "../ContactsService";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { selectIsFirstNameMandatory } from "redux/generalSlice";
import { KeyboardBackspace } from "@mui/icons-material";
import { RouterLink } from "components/router-link";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";
import ExcelIcon from "assets/icons1/ExcelIcon.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

const ImportContacts = () => {
  useTitle(title.IMPORT_CONTACTS);
  const userEmail = useAppSelector(selectEmail);
  const [managingStep, setManagingStep] = useState<string[]>([]);
  const [formFields, setFormFields] = useState<any>(uploadContactsField());
  const [parsingCsvData, setParsingCsvData] = useState<any>({
    isOpen: false,
    csvData: {},
  });
  const [isColumnMatch, setColumnMatch] = useState<boolean>(false);
  const [columnMapperJson, setColumnMapperJson] = useState<any>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const isFirstNameMandatory = useAppSelector(selectIsFirstNameMandatory);
  const [csvColumn, setCsvColumn] = useState<
    {
      name: string;
      field: string;
      type: string;
      fieldMapping: string;
      required: boolean;
      error: string;
    }[]
  >([
    {
      name: "Email",
      field: "emailId",
      type: "String",
      fieldMapping: "",
      required: true,
      error: "",
    },
    {
      name: "First Name",
      field: "firstName",
      type: "String",
      fieldMapping: "",
      required: isFirstNameMandatory ?? true,
      error: "",
    },
    {
      name: "Last Name",
      field: "lastName",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "Contact No",
      field: "contactNo",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "Address",
      field: "address",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "Postal Code",
      field: "postalCode",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "City",
      field: "city",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "State",
      field: "state",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "Country",
      field: "state",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "Tags",
      field: "tagName",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
    {
      name: "Time Zone",
      field: "timeZone",
      type: "String",
      fieldMapping: "",
      required: false,
      error: "",
    },
  ]);
  const [mappingData, setMappingData] = useState({});
  const [editMappingStep, setEditMappingStep] = useState<boolean>(true);
  const [editGroupNameStep, setEditGroupNameStep] = useState<boolean>(true);
  const [existingAttributesUpdate, setExistingAttributesUpdate] =
    useState<boolean>(false);
  const [eraseExisting, setEraseExisting] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const isAllOtherStepsCompleted = managingStep.includes(
    strings.UploadContactsSteps.CsvConfirmed &&
      strings.UploadContactsSteps.DataMapped &&
      strings.UploadContactsSteps.NameSelected
  );

  const handleFieldChange = (fieldName: string, updatedValue: any) => {
    if (fieldName === "groupCSV") {
      setFormFields({
        ...formFields,
        [fieldName]: {
          value: updatedValue,
          error: "",
        },
      });
    }
  };

  const handleDeleteDropZoneFile = () => {
    setManagingStep([]);
    setEditMappingStep(true);
    setEditGroupNameStep(true);
    handleFieldChange("groupCSV", "");
    setColumnMatch(false);
    setFormFields(uploadContactsField());
    setChecked(false);
    setEraseExisting(false);
    setExistingAttributesUpdate(false);
  };

  const handleValidation = () => {
    const { errors, isValid } = uploadContactsValidation(formFields);
    setFormFields({ ...errors });
    return isValid;
  };

  const validateFieldMapping = () => {
    let isValid = true;
    csvColumn.forEach((item: any) => {
      if (item.required && !isTruthy(item.fieldMapping)) {
        setCsvColumn((prev: any) =>
          prev.map((data: any) => {
            if (data.field === item.field) {
              data.error = `Please select ${item.name}!`;
            }
            return data;
          })
        );
        isValid = false;
      }
    });
    return isValid;
  };

  const handleConfirmMapping = () => {
    const contactGroupUpload: any = {};
    csvColumn.forEach((item: any) => {
      if (isTruthy(item.fieldMapping)) {
        contactGroupUpload[item.fieldMapping] = item.field;
      }
    });
    setMappingData(contactGroupUpload);
    setEditMappingStep(false);
    setManagingStep([...managingStep, strings.UploadContactsSteps.DataMapped]);
  };

  const handleConfirmGroupName = () => {
    setEditMappingStep(false);
    setEditGroupNameStep(false);
    setManagingStep([
      ...managingStep,
      strings.UploadContactsSteps.NameSelected,
    ]);
  };

  const uploadContactMailingList = async () => {
    if (formFields.groupName.value.length <= 50 && handleValidation()) {
      try {
        setLoading(true);
        const data = new FormData();
        const file = formFields?.groupCSV?.value;
        data.append("file", file);
        data.append("groupName", formFields.groupName.value.trim());
        data.append("columnMapperJson", JSON.stringify(mappingData));
        data.append("public", formFields.groupType.value);
        data.append(
          "existingAttributesUpdate",
          existingAttributesUpdate.toString()
        );
        data.append("eraseExisting", eraseExisting.toString());
        const response = await uploadContactGroups(data);
        openSuccessNotification(response.message);
        history.push(viewpaths.contactsViewPath);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        if (error.message.includes("Failed to fetch")) {
          openErrorNotification(
            "Your file name is incorrect or the uploaded file has been changed."
          );
        }
        handleErrorMessage(error);
      }
    }
  };

  const getHeader = () => {
    return (
      <Stack direction="column" px={2}>
        <Stack direction="column" spacing={1} pb={2}>
          <Stack direction="row" alignItems="center">
            <Link
              color="text.primary"
              href={viewpaths.contactsViewPath}
              sx={{
                alignItems: "center",
                display: "inline-flex",
                textDecoration: "none !important",
              }}
              onClick={(e) => {
                e.preventDefault();
                history.push(viewpaths.contactsViewPath);
              }}
              underline="hover"
              component={RouterLink}
            >
              <KeyboardBackspace
                htmlColor="#475569"
                sx={{ cursor: "pointer", pr: 1 }}
              />
              <Typography variant="h4" color="text.primary">
                Import contacts from a file
              </Typography>
            </Link>
          </Stack>

          <Typography color="text.secondary">
            Import your contacts effortlessly by uploading a file with their
            details, ideal for handling large contact lists.
          </Typography>
        </Stack>
        <Divider />
      </Stack>
    );
  };

  const getUploadContactStep = () => {
    return (
      <Stack direction="column" spacing={2} pb={1}>
        <Stack direction="column" spacing={1}>
          <Box display="flex" alignItems="center">
            <Typography variant="h4" color="text.primary">
              Upload your file
            </Typography>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="text.secondary">
              Choose a file to import your contacts along with their details.
            </Typography>
            {!isTruthy(formFields?.groupCSV?.value?.name) && (
              <Box>
                <Button
                  id="download_csv_btn"
                  variant="text"
                  size="small"
                  startIcon={
                    <Stack>
                      <img src={DownloadCSV_Icon} alt="Export CSV" />
                    </Stack>
                  }
                  disabled={isLoading}
                  onClick={ExportCSV(
                    [
                      "EMAIL_ID",
                      "FIRST_NAME",
                      "LAST_NAME",
                      "CONTACT_NO",
                      "ADDRESS",
                      "POSTAL_CODE",
                      "CITY",
                      "STATE",
                      "COUNTRY",
                      "TAGS",
                      "TIME_ZONE",
                    ],
                    "example-contact-list",
                    userEmail
                  )}
                >
                  Download example file (.csv)
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
        {!isTruthy(formFields?.groupCSV?.value?.name) ? (
          <Grid container>
            <AddContactDropezoneComponent />
          </Grid>
        ) : (
          <>
            {managingStep.includes(strings.UploadContactsSteps.CsvConfirmed) ? (
              <>
                <Grid container spacing={2}>
                  <Grid
                    item
                    sx={{
                      backgroundColor: "#F4F1FC",
                      border: "1px solid #E2D9F5",
                      borderRadius: "6px",
                    }}
                    xs={12}
                    md={4}
                    p={1.5}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack>
                          <img src={ExcelIcon} width="25px" alt="excel icon" />
                        </Stack>
                        <Stack>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {formFields?.groupCSV?.value?.name}
                          </Typography>
                          <Typography color="text.primary">
                            {formFields?.groupCSV?.value?.size / (1024 * 1024) <
                            0.5
                              ? Math.round(
                                  Math.max(
                                    1,
                                    formFields?.groupCSV?.value?.size / 1024
                                  )
                                ) + " KB"
                              : (
                                  formFields?.groupCSV?.value?.size /
                                  (1024 * 1024)
                                ).toFixed(2) + " MB"}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" alignItems="center">
                        <IconButton onClick={() => handleDeleteDropZoneFile()}>
                          <CloseIcon htmlColor="#7551D0" fontSize="medium" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid
                    item
                    sx={{
                      backgroundColor: "#F4F1FC",
                      border: "1px solid #E2D9F5",
                      borderRadius: "6px",
                    }}
                    xs={12}
                    md={4}
                    p={1.5}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack>
                          <img src={ExcelIcon} width="25px" />
                        </Stack>
                        <Stack>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {formFields?.groupCSV?.value?.name}
                          </Typography>
                          <Typography color="text.primary">
                            {formFields?.groupCSV?.value?.size / (1024 * 1024) <
                            0.5
                              ? Math.round(
                                  Math.max(
                                    1,
                                    formFields?.groupCSV?.value?.size / 1024
                                  )
                                ) + " KB"
                              : (
                                  formFields?.groupCSV?.value?.size /
                                  (1024 * 1024)
                                ).toFixed(2) + " MB"}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" alignItems="center">
                        <IconButton onClick={() => handleDeleteDropZoneFile()}>
                          <CloseIcon htmlColor="#7551D0" fontSize="medium" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
                <Stack>
                  <Box display="flex" justifyContent="center">
                    <Button
                      variant="contained"
                      onClick={() => {
                        setManagingStep([
                          ...managingStep,
                          strings.UploadContactsSteps.CsvConfirmed,
                        ]);
                      }}
                    >
                      Confirm your file
                    </Button>
                  </Box>
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    );
  };

  const getMappingDataStep = () => {
    const isUploadContactStepCompleted = managingStep.includes(
      strings.UploadContactsSteps.CsvConfirmed
    );
    return (
      <Stack
        direction="column"
        spacing={2}
        pb={1}
        sx={{ opacity: isUploadContactStepCompleted ? 1 : 0.5 }}
      >
        <Stack direction="column" spacing={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" color="text.primary">
              Mapping data
            </Typography>
            {!editMappingStep && (
              <Typography
                component={"a"}
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => setEditMappingStep(true)}
              >
                Edit
              </Typography>
            )}
          </Box>
          {isUploadContactStepCompleted && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography color="text.secondary">
                Select the column name that corresponds to the contact
                attribute.
              </Typography>
            </Stack>
          )}
        </Stack>
        {isUploadContactStepCompleted && editMappingStep && (
          <Grid container>
            <DynamicCsvFieldMapping />
          </Grid>
        )}
        {isUploadContactStepCompleted && editMappingStep && (
          <Stack>
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                onClick={() => validateFieldMapping() && handleConfirmMapping()}
              >
                Confirm mapping
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    );
  };

  const getCreateGroupStep = () => {
    const isAllOtherStepsCompleted = managingStep.includes(
      strings.UploadContactsSteps.CsvConfirmed &&
        strings.UploadContactsSteps.DataMapped
    );
    const groupType = formFields?.groupType?.value === 0 ? "Private" : "Public";
    return (
      <Stack
        direction="column"
        spacing={2}
        pb={1}
        sx={{ opacity: isAllOtherStepsCompleted ? 1 : 0.5 }}
      >
        <Stack direction="column" spacing={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" color="text.primary">
              Create a group
            </Typography>
            {!editGroupNameStep && (
              <Typography
                component={"a"}
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => setEditGroupNameStep(true)}
              >
                Edit
              </Typography>
            )}
          </Box>
          {!editGroupNameStep && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography color="text.secondary">
                {`${formFields.groupName.value} (${groupType})`}
              </Typography>
            </Stack>
          )}
        </Stack>

        {isAllOtherStepsCompleted && editGroupNameStep && !editMappingStep && (
          <Grid container>
            <GroupNameTypeSelection />
          </Grid>
        )}
        {isAllOtherStepsCompleted && editGroupNameStep && !editMappingStep && (
          <Stack>
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                onClick={() => handleValidation() && handleConfirmGroupName()}
              >
                Confirm your group
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    );
  };

  const getFinalStep = () => {
    return (
      isAllOtherStepsCompleted &&
      !editMappingStep &&
      !editGroupNameStep && (
        <>
          <Divider />
          <Stack
            direction="column"
            spacing={2}
            pb={1}
            sx={{ opacity: isAllOtherStepsCompleted ? 1 : 0.5 }}
          >
            <Stack direction="column" spacing={1}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h4" color="text.primary">
                  Finalize your import
                </Typography>
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography color="text.secondary">
                  Contacts management
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center">
              <CustomSwitch
                checked={existingAttributesUpdate}
                onChange={() =>
                  setExistingAttributesUpdate(!existingAttributesUpdate)
                }
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color="text.primary"
                  pl={1}
                >
                  Update the attributes of the existing contacts
                </Typography>
                <CustomInfoTooltip
                  placement="bottom"
                  infoMessage={
                    "When enabled, any existing attributes will be overwritten if the contacts in your import already exist. When disabled, these attributes will remain unchanged."
                  }
                />
              </Stack>
            </Stack>
            {existingAttributesUpdate && (
              <Stack direction="row" alignItems="center" pl={5}>
                <CustomSwitch
                  checked={eraseExisting}
                  onChange={() => setEraseExisting(!eraseExisting)}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.primary"
                    pl={1}
                  >
                    Import empty fields to erase existing attribute data.
                  </Typography>
                  <CustomInfoTooltip
                    placement="bottom"
                    infoMessage={
                      "If enabled, empty fields in your import will erase any contact attributes with existing data. If disabled, empty fields will not affect your current data."
                    }
                  />
                </Stack>
              </Stack>
            )}

            {isAllOtherStepsCompleted &&
              isAllOtherStepsCompleted &&
              !editMappingStep &&
              !editGroupNameStep && (
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography color="text.secondary">
                      Opt-in Agreement
                    </Typography>
                  </Stack>
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={checked}
                        onChange={() => setChecked(!checked)}
                        sx={{
                          padding: "0px !important",
                          mr: 0.5,
                        }}
                      />
                      <Typography variant="body1" color="text.primary">
                        I confirm that my new import adheres to these
                        conditions:
                      </Typography>
                    </Stack>
                    <Stack direction="column" spacing={1} sx={{ pl: 2.5 }}>
                      <Typography variant="body2" color="text.primary">
                        • My contacts explicitly gave me their permission to
                        send Email (newsletter) campaigns within the last two
                        years, or had been asked to within the last two years.
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        • These contacts were not borrowed from a third party.
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        • These contacts were not purchased or rented.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2" color="text.primary">
                        We may suspend or cancel any email campaigns sent to
                        contacts that don't adhere to these conditions at any
                        time.
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box display="flex" justifyContent="center" pt={2}>
                    <Button
                      variant="contained"
                      onClick={() => uploadContactMailingList()}
                      disabled={!checked}
                    >
                      Confirm your import
                    </Button>
                  </Box>
                </Stack>
              )}
          </Stack>
        </>
      )
    );
  };

  const getBody = () => {
    return (
      <Stack direction="column" spacing={4} px={2}>
        {getUploadContactStep()}
        <Divider />
        {getMappingDataStep()}
        <Divider />
        {getCreateGroupStep()}
        {getFinalStep()}
      </Stack>
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        {getHeader()}
        {getBody()}
      </Stack>
    );
  };
  return (
    <CenteredAuthenticatedLayout>
      <ContactContext.Provider
        value={{
          formFields,
          setFormFields,
          parsingCsvData,
          setParsingCsvData,
          columnMapperJson,
          setColumnMapperJson,
          isColumnMatch,
          setColumnMatch,
          csvColumn,
          setCsvColumn,
          setManagingStep,
          managingStep,
        }}
      >
        {getPage()}
      </ContactContext.Provider>
      <CustomLoader isLoading={isLoading} />
    </CenteredAuthenticatedLayout>
  );
};

export default ImportContacts;
